import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import useAuth from '@/composables/useAuth';
import { FEATURE_FLAG, PERMANENT_FLAGS } from '@/constants/feature_flags';
import { useBillingStore, useFeatureFlagStore } from '@/stores';

import { API_TABS } from './constants';
import type { ApiTabValueType } from './types';

export default function useApiTabAccess() {
  const { hasFeatureFlag } = useFeatureFlagStore();
  const { isConnectorTier, isEnterpriseTier, isSupernodeOrEnterpriseTier } =
    storeToRefs(useBillingStore());
  const { hasScope } = useAuth();

  const hasWebhooksAccess = computed(
    () =>
      (isEnterpriseTier.value ||
        hasFeatureFlag(PERMANENT_FLAGS.WEBHOOK_ACCESS)) &&
      hasScope('read:integrations'),
  );

  const hasIntegrationsAccess = computed(
    () =>
      (isSupernodeOrEnterpriseTier.value ||
        hasFeatureFlag(PERMANENT_FLAGS.REST_API)) &&
      hasScope('read:oauth-applications'),
  );

  const hasFiltersTierAccess = computed(
    () =>
      isSupernodeOrEnterpriseTier.value ||
      (isConnectorTier.value &&
        hasFeatureFlag(PERMANENT_FLAGS.GENERATE_INTEGRATION_OVERLAPS)),
  );

  const hasFiltersAccess = computed(
    () => hasFiltersTierAccess.value && hasScope('read:integrations'),
  );

  const hasKeysAccess = computed(
    () =>
      hasFeatureFlag(FEATURE_FLAG.TEMP_API_KEYS) &&
      isSupernodeOrEnterpriseTier.value &&
      hasScope('read:api-keys'),
  );

  const accessibleTabs = computed(() => {
    const hasAccessByTab: Record<ApiTabValueType, boolean> = {
      webhooks: hasWebhooksAccess.value,
      integrations: hasIntegrationsAccess.value,
      filters: hasFiltersAccess.value,
      keys: hasKeysAccess.value,
    };
    return API_TABS.filter((tab) => hasAccessByTab[tab]);
  });

  const hasApiPageAccess = computed(
    () =>
      hasFeatureFlag(FEATURE_FLAG.TEMP_API_CONTROLS) &&
      accessibleTabs.value.length > 0,
  );

  function getTabRedirect(tab: string | string[] | undefined) {
    if (!hasApiPageAccess.value) return { name: 'not_found' };

    const isTabAccessible = accessibleTabs.value.some(
      (accessibleTab) => accessibleTab === tab,
    );
    if (isTabAccessible) return undefined;

    return { name: 'api', params: { tab: accessibleTabs.value[0] } };
  }

  return { accessibleTabs, hasApiPageAccess, getTabRedirect };
}
